/**
 * ScriptsList - Full-width scrollable list of script cards
 *
 * Design principles (Script-Centric Redesign):
 * - Full-width cards with max-width for readability
 * - Centered content layout (max-w-4xl mx-auto)
 * - Better spacing between cards (space-y-4)
 * - Auto-scroll to selected item
 * - Empty state with clear guidance
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NarrationScriptCard, AudioStatus } from './NarrationScriptCard';
import { FileText, Loader2 } from 'lucide-react';
import type { SlideOutline } from '@/types/outline';
import type { SlideAudioStatusSummary } from '@/services/audioService';

export interface SlideScript {
  slideId: string;
  slideIdentifier: string;
  childConversationId: string;
  title: string;
  script: {
    scriptText: string | null;
    promptFlash: string | null;
    promptPro: string | null;
    preferredModel: string | null;
    audioUrl: string | null;
    audioDuration: number | null;
    updatedAt: string | null;
  } | null;
  hasAudio: boolean;
}

/** Progress state for apply operations */
export interface ApplyProgress {
  thinking?: string;
  currentOperation?: string;
  percentage?: number;
}

interface ScriptsListProps {
  outlineSlides: SlideOutline[];
  scripts: SlideScript[];
  audioStatus: SlideAudioStatusSummary[];
  generatingSlideIds: Set<string>;
  audioProgressMap?: Record<string, number>;
  selectedSlideId: string | null;
  currentlyPlayingSlideId: string | null;
  isLoading?: boolean;
  onSelectSlide: (slideId: string) => void;
  onGenerateAudio: (slideId: string) => void;
  onPlayChange: (slideId: string, playing: boolean) => void;
  onScriptSave?: (slideId: string, text: string) => void;
  isSavingScript?: boolean;
  /** Set of slide IDs currently having audio applied */
  applyingSlideIds?: Set<string>;
  /** Progress state for each slide being applied */
  slideApplyProgress?: Record<string, ApplyProgress>;
  /** Compact mode for narrower panels (280px) - uses smaller typography and simpler layout */
  compact?: boolean;
  className?: string;
}

export const ScriptsList: React.FC<ScriptsListProps> = ({
  outlineSlides,
  scripts,
  audioStatus,
  generatingSlideIds,
  audioProgressMap = {},
  selectedSlideId,
  currentlyPlayingSlideId,
  isLoading,
  onSelectSlide,
  onGenerateAudio,
  onPlayChange,
  onScriptSave,
  isSavingScript,
  applyingSlideIds,
  slideApplyProgress,
  compact = false,
  className,
}) => {
  const selectedCardRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastUserClickedSlideRef = useRef<string | null>(null);
  const isUserClickRef = useRef(false);
  const savedScrollTopRef = useRef<number>(0);

  const getScriptForSlide = useCallback((outlineSlideId: string): SlideScript | undefined => {
    return scripts.find(s => s.slideIdentifier === outlineSlideId);
  }, [scripts]);

  const getAudioStatusForSlide = useCallback((slideId: string): SlideAudioStatusSummary | undefined => {
    return audioStatus.find(s => s.slideIdentifier === slideId || s.slideId === slideId);
  }, [audioStatus]);

  const getAudioProgressForSlide = useCallback((outlineSlideId: string, dbSlideId?: string): number | undefined => {
    // Check by outline slide id first, then by db slide id
    if (audioProgressMap[outlineSlideId] !== undefined) {
      return audioProgressMap[outlineSlideId];
    }
    if (dbSlideId && audioProgressMap[dbSlideId] !== undefined) {
      return audioProgressMap[dbSlideId];
    }
    return undefined;
  }, [audioProgressMap]);

  const getApplyProgressForSlide = useCallback((dbSlideId?: string): ApplyProgress | undefined => {
    if (!dbSlideId || !slideApplyProgress) return undefined;
    return slideApplyProgress[dbSlideId];
  }, [slideApplyProgress]);

  const isSlideApplying = useCallback((dbSlideId?: string): boolean => {
    if (!dbSlideId || !applyingSlideIds) return false;
    return applyingSlideIds.has(dbSlideId);
  }, [applyingSlideIds]);

  const mapAudioStatus = (status?: SlideAudioStatusSummary): AudioStatus => {
    if (!status?.audioStatus) return 'none';
    switch (status.audioStatus) {
      case 'COMPLETED': return 'completed';
      case 'GENERATING': return 'generating';
      case 'ALIGNING': return 'aligning';
      case 'FAILED': return 'failed';
      case 'PENDING': return 'pending';
      default: return 'none';
    }
  };

  // Auto-scroll to selected card only when selection changes programmatically
  // (not when user clicks - the card they clicked is already in view)
  useEffect(() => {
    if (selectedSlideId && selectedCardRef.current) {
      // Skip scroll if user just clicked on this card
      if (isUserClickRef.current && lastUserClickedSlideRef.current === selectedSlideId) {
        isUserClickRef.current = false;
        return;
      }

      // Small delay to ensure DOM is ready after any state updates
      const timeoutId = setTimeout(() => {
        if (selectedCardRef.current) {
          selectedCardRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedSlideId]);

  // Wrap onSelectSlide to track user clicks and preserve scroll position
  const handleSelectSlide = useCallback((slideId: string) => {
    // Save current scroll position before state update
    const scrollViewport = scrollContainerRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollViewport) {
      savedScrollTopRef.current = scrollViewport.scrollTop;
    }

    isUserClickRef.current = true;
    lastUserClickedSlideRef.current = slideId;
    onSelectSlide(slideId);

    // Restore scroll position after a microtask to handle any layout shifts
    requestAnimationFrame(() => {
      if (scrollViewport && isUserClickRef.current) {
        scrollViewport.scrollTop = savedScrollTopRef.current;
      }
    });
  }, [onSelectSlide]);

  const activeSlides = outlineSlides.filter(s => !s.isDeleted);

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center py-20", className)}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading scripts...</span>
        </div>
      </div>
    );
  }

  if (activeSlides.length === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center py-20 text-center", className)}>
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400 dark:text-zinc-500" />
        </div>
        <p className="text-sm font-medium text-gray-600 dark:text-zinc-400">
          No slides yet
        </p>
        <p className="text-xs text-gray-400 dark:text-zinc-500 mt-1">
          Create an outline to start writing scripts
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="h-full">
      <ScrollArea className="h-full">
        {/* Container - narrower in compact mode for studio panel */}
        <div
          className={cn(
            "mx-auto",
            compact ? "px-2 py-2" : "max-w-2xl px-4 py-4"
          )}
          style={{ overflowAnchor: 'none' }}
        >
          <div className={cn("space-y-4", compact && "space-y-2")}>
          {activeSlides.map((slide, index) => {
            const script = getScriptForSlide(slide.id);
            const status = getAudioStatusForSlide(slide.id);
            const slideId = script?.slideId || slide.id;
            const isSelected = selectedSlideId === slide.id;
            const isPlaying = currentlyPlayingSlideId === slideId;
            const isForceGenerating = script?.slideId ? generatingSlideIds.has(script.slideId) : false;

            return (
              <div
                key={slide.id}
                ref={isSelected ? selectedCardRef : undefined}
              >
                <NarrationScriptCard
                  slideId={script?.slideId || ''}
                  slideNumber={index + 1}
                  title={slide.title}
                  scriptText={script?.script?.scriptText || null}
                  thumbnailUrl={slide.generatedThumbnail}
                  audioStatus={mapAudioStatus(status)}
                  audioProgress={getAudioProgressForSlide(slide.id, script?.slideId)}
                  audioDuration={status?.audioDuration || script?.script?.audioDuration || undefined}
                  audioVersion={status?.version || undefined}
                  isSelected={isSelected}
                  isPlaying={isPlaying}
                  isForceGenerating={isForceGenerating}
                  isApplying={isSlideApplying(script?.slideId)}
                  applyProgress={getApplyProgressForSlide(script?.slideId)}
                  onSelect={() => handleSelectSlide(slide.id)}
                  onGenerateAudio={() => {
                    if (script?.slideId) {
                      onGenerateAudio(script.slideId);
                    }
                  }}
                  onPlayAudio={() => {
                    if (slideId) {
                      // Auto-select the card when play is clicked
                      if (!isSelected) {
                        handleSelectSlide(slide.id);
                      }
                      onPlayChange(slideId, !isPlaying);
                    }
                  }}
                  onScriptSave={onScriptSave ? (text) => {
                    if (script?.slideId) {
                      onScriptSave(script.slideId, text);
                    }
                  } : undefined}
                  isSavingScript={isSavingScript}
                  compact={compact}
                />
              </div>
            );
          })}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default ScriptsList;
